import React, { Fragment } from "react";
import { StepRenderProps } from "../../../interfaces/FlowInterfaces";
import { createStyles, makeStyles, TableCell, Theme, Typography } from "@material-ui/core";
import { observer } from "mobx-react";
import { Alert, AlertTitle } from "@material-ui/lab";
import { VolunteerRegistrationFlowExternalState, VolunteerSelectionStepValues } from "../../_interfaces/IAdminVolunteerRegistrationStepTypeValues";
import { IAdminVolunteerRegistrationFlowValues } from "../../_interfaces/IAdminVolunteerRegistrationFlowValues";
import FilterChips from "../../../../../components/Shared/FilterChips";
import RecordTable from "../../../../../components/Shared/Table/NewRecordTable";
import REMChip from "../../../../../shared/modules/rem-conversion/components/Chip";
import { IdentifiableObject } from "../../../../../stores/models/OptionCollection";
import { IVolunteerOverview } from "../../../../../stores/models/VolunteerOverview";
import { ISortableTableHeader, TableHeader } from "../../../../../stores/models/TableHeader";
import { Option } from "../../../../../stores/models/Option";
import VolunteerSearchAndFilterToolbar from "../../../../../components/AccountSettings/Pages/Organization/Volunteers/VolunteerSearchAndFilterToolbar";
import TableToolbar from "../../../../../components/Shared/Table/TableToolbar";
import clsx from "clsx";
import { StylizedInternalLink } from "../../../../../components/Navigation/Components";
import { useVolunteerSelectionController } from "../../hooks/useVolunteerSelectionController";
import CheckboxTableCell from "../../../../../components/Shared/Table/CheckboxTableCell";
import { useStandardStyles } from "../../../../../shared/general/hooks/useStandardStyles";
import { DialogState } from "../../../../../stores/models/DialogState";
import { Shift } from "../../../../../stores/models/Shift";
import FormLoadingSpinner from "../../../../../components/Organization/VolunteerOpportunities/FormLoadingSpinner";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        header: {
            marginBottom: theme.spacing(2),
        },
        section: {
            marginBottom: theme.spacing(2),
        },
        alert: {
            marginBottom: theme.spacing(2),
            boxSizing: 'border-box',
            textAlign: 'left',
            flexGrow: 1
        },
        avatar: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            width: '75px',
            height: '75px',
            margin: '16px auto',
            marginTop: theme.spacing(4),
        },
        prompt: {
            marginTop: theme.spacing(2),
            color: theme.palette.action.active
        },
        filterToolbar: {
            marginTop: theme.spacing(0),
            [theme.breakpoints.down('sm')]: {
                marginBottom: theme.spacing(1)
            }
        },
        selectionToolbar: {
            [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(-1)
            },
            marginTop: theme.spacing(-1.5)
        },
        noWrap: {
            display: 'flex',
            flexWrap: 'nowrap',
            margin: '-6px 0px'
        },
        chip: {
            '& .MuiChip-icon, .MuiChip-label': {
                color: theme.palette.darkBackground.main
            },
            '&:not(:last-child)': {
                marginRight: theme.spacing(.5),
            },
            marginBottom: theme.spacing(.5),
        },
        tableChip: {
            backgroundColor: theme.palette.primaryBackground.main,
        },
        link: {
            color: theme.palette.primary.main
        },
        viewButton: {
            marginLeft: theme.spacing(1)
        },
    })
);

const MAX_ROLE_CHIPS_TO_DISPLAY = 2;

const headCells = ([
    { id: 'checkbox', alignment: 'center', disablePadding: true, sortable: false, checkbox: true },
    { id: 'firstName', alignment: 'left', disablePadding: false, label: 'First Name', sortable: true, sortValue: 'firstName' },
    { id: 'lastName', alignment: 'left', disablePadding: false, label: 'Last Name', sortable: true, sortValue: 'lastName' },
    { id: 'status', alignment: 'left', disablePadding: false, label: 'Status', sortable: true, sortValue: 'status' },
    { id: 'roles', alignment: 'left', disablePadding: false, label: 'Roles', sortable: false },
] as ISortableTableHeader<IVolunteerOverview>[]);

const volunteerTableHeaderCells = headCells.map(headCell => new TableHeader(headCell));

const dataCell = (key: keyof IVolunteerOverview) => {
    return (option: Option<IVolunteerOverview>, rowIndex: number, cellIndex: number) => {
        return (
            <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>{option.object[key]}</TableCell>
        );
    }
};

export const VolunteerSelectionStep: React.FC<
    StepRenderProps<VolunteerSelectionStepValues, IAdminVolunteerRegistrationFlowValues, VolunteerRegistrationFlowExternalState>
> = observer(({
    values,
    update,
    submit,
    canSubmit,
    validation,
    allValues,
    externallyManagedState
}) => {

    /********* React hooks *********/

    const classes = useStyles();
    const baseClasses = useStandardStyles();
    const controller = useVolunteerSelectionController(
        values,
        allValues,
        externallyManagedState
            ? externallyManagedState
            : { registeredVolunteerIds: [], topLevelShift: new Shift(), dialogState: new DialogState() },
        update,
        submit
    );

    const tableController = controller.values.volunteersTableController;

    const onClearSelections = () => {
        tableController?.clearSelections();
    }

    const onSearchInputChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.target) {
            const searchQuery = event?.target.value;
            tableController?.tableState.filters.setSearchQuery(searchQuery);
        }
    }

    const onCheckboxClicked = (event: React.MouseEvent<HTMLButtonElement | HTMLTableHeaderCellElement, MouseEvent>, option: Option<IVolunteerOverview>) => {
        event.stopPropagation();
        if (!option.disabled) {
            tableController?.toggleOptionSelection(option);
        }
    }

    /********* Render *********/

    if (!tableController) {
        return <FormLoadingSpinner />;
    }

    return (
        <Fragment>
            {/* Error Notification */}
            {controller.fieldErrors && Object.values(controller.fieldErrors).length > 0
                ? <Alert severity="error" className={classes.alert}>
                    <AlertTitle>Registration Error</AlertTitle>
                    {controller.fieldErrors.volunteerIds}
                </Alert>
                : null
            }
            {controller.error
                ? <Alert severity="error" className={classes.alert}>
                    <AlertTitle>Registration Error</AlertTitle>
                    {controller.error}
                </Alert>
                : null
            }
            {/* Header */}
            <div>
                <Typography variant="h5" className={classes.header}>Volunteers</Typography>
            </div>
            {/* Selected Volunteers Count */}
            {tableController.persistentSelections
                ? <div className={classes.section}>
                    <Typography className={baseClasses.bold}>Selected Volunteers</Typography>
                    <div className={baseClasses.flex}>
                        <Typography>
                            {
                                tableController.persistentSelections.options.length === 1
                                    ? `${tableController.persistentSelections.options.length} volunteer selected`
                                    : `${tableController.persistentSelections.options.length} volunteers selected`
                            }
                        </Typography>
                        {tableController.persistentSelections.options.length > 0
                            ? <StylizedInternalLink
                                onClick={() => {
                                    update({ viewVolunteers: true })
                                    submit({ viewVolunteers: true })
                                }}
                                children={<Typography className={clsx(classes.link, classes.viewButton)}>View Selected</Typography>}
                            />
                            : null
                        }
                    </div>

                    {/* <FormControlLabel
                                control={
                                    <Switch
                                        checked={controller.stepBoundValues.displaySelectionsOnly}
                                        onChange={(event, checked) => { controller.stepBoundValues.setDisplaySelectionsOnly(checked) }}
                                        name="display-selections-only"
                                        color="primary"
                                    />
                                }
                                labelPlacement="start"
                                label="Display selections only"
                            /> */}
                    {/* {tableController.persistentSelections.options.map(option => {
                        return (
                            <REMChip
                                variant="outlined"
                                label={`${option.object.firstName} ${option.object.lastName}`}
                                onDelete={() => tableController.toggleOptionSelection(option)}
                                className={classes.chip}
                            />
                        )
                    })} */}
                </div>
                : null
            }
            {/* Toolbar Buttons */}
            <TableToolbar
                leftButtons={
                    <VolunteerSearchAndFilterToolbar
                        searchQuery={tableController.tableState.filters.searchQuery}
                        roleOptions={controller.stepBoundValues.roleOptions}
                        statusOptions={controller.stepBoundValues.statusOptions}
                        onSearchInputChanged={onSearchInputChanged}
                    />
                }
                SelectionIndicatorProps={{
                    numSelected: tableController.numSelectedIncludingUnloaded,
                    onClearSelections: onClearSelections
                }}
            />
            {/* Active Filters */}
            <FilterChips
                filters={{
                    'Status': {
                        selections: controller.stepBoundValues.statusOptions.selections as Option<IdentifiableObject<'id'>>[],
                        getLabel: (optionObject) => (optionObject as unknown as { status: string }).status
                    },
                    'Role': {
                        selections: controller.stepBoundValues.roleOptions.selections,
                        getLabel: (optionObject) => (optionObject as unknown as { position: string }).position
                    }
                }}
                className={classes.filterToolbar}
            />
            {/* Volunteer Table */}
            <div className={clsx(baseClasses.flex, baseClasses.flexColumn, baseClasses.flexGrow, baseClasses.overflowXAuto, baseClasses.relative)}>
                {tableController.tableState.loading ? <FormLoadingSpinner /> : null}
                <RecordTable
                    controller={tableController}
                    tableHeaderCells={volunteerTableHeaderCells}
                    disableSelectAllRow
                    displayFunctions={[
                        (option: Option<IVolunteerOverview>, rowIndex: number, cellIndex: number) => {
                            return (
                                <CheckboxTableCell
                                    key={`${rowIndex}-${cellIndex}`}
                                    CheckboxProps={{
                                        checked: option.disabled || option.selected,
                                        disabled: option.disabled
                                    }}
                                    onCheckboxClicked={(event) => onCheckboxClicked(event, option)}
                                />
                            )
                        },
                        dataCell('firstName'),
                        dataCell('lastName'),
                        dataCell('status'),
                        (option: Option<IVolunteerOverview>, rowIndex: number, cellIndex: number) => {
                            return (
                                <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                                    <div className={classes.noWrap}>
                                        {option.object.roles.map((role, index) => {
                                            if (index < MAX_ROLE_CHIPS_TO_DISPLAY) {
                                                return (
                                                    <REMChip
                                                        label={role.position}
                                                        className={clsx(classes.chip, classes.tableChip)}
                                                        key={`role-chip-${index}`}
                                                    />
                                                )
                                            } else if (index === MAX_ROLE_CHIPS_TO_DISPLAY) {
                                                return (
                                                    <REMChip
                                                        label={`+${option.object.roles.length - MAX_ROLE_CHIPS_TO_DISPLAY}`}
                                                        className={clsx(classes.chip, classes.tableChip)}
                                                        key={`role-chip-${index}`}
                                                    />
                                                )
                                            }
                                        })}
                                    </div>
                                </TableCell>
                            );
                        }
                    ]}
                    onRowClicked={(option: Option<IVolunteerOverview>, rowIndex: number) => {
                        if (!option.disabled) {
                            tableController.toggleOptionSelection(option);
                        }
                    }}
                />
            </div>
        </Fragment >
    );
});